import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import { registerUnauthorizedHandler } from "../api/client"
import { type AuthUser, logout as apiLogout, getMe } from "../api/endpoints/auth"
import type { SessionUser } from "../types/auth"

interface AuthContextValue {
  user: SessionUser | null
  isLoading: boolean
  setSession: (token: string, user: AuthUser, expiresAt: string) => void
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

function readStoredUser(): SessionUser | null {
  const raw = localStorage.getItem("session_user")
  return raw ? (JSON.parse(raw) as SessionUser) : null
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<SessionUser | null>(() => readStoredUser())
  const [isLoading, setIsLoading] = useState<boolean>(
    Boolean(localStorage.getItem("session_token")),
  )

  const clearSession = useCallback(() => {
    localStorage.removeItem("session_token")
    localStorage.removeItem("session_user")
    setUser(null)
  }, [])

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("session_token")
    if (!token) {
      setIsLoading(false)
      setUser(null)
      return
    }

    try {
      const response = await getMe()
      const nextUser: SessionUser = {
        ...response.data,
        sessionExpiresAt: response.data.sessionExpiresAt,
      }
      localStorage.setItem("session_user", JSON.stringify(nextUser))
      setUser(nextUser)
    } catch {
      clearSession()
    } finally {
      setIsLoading(false)
    }
  }, [clearSession])

  const setSession = useCallback((token: string, authUser: AuthUser, expiresAt: string) => {
    const nextUser: SessionUser = {
      ...authUser,
      sessionExpiresAt: expiresAt,
    }
    localStorage.setItem("session_token", token)
    localStorage.setItem("session_user", JSON.stringify(nextUser))
    setUser(nextUser)
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiLogout()
    } catch {
      // Intentionally swallow logout API errors and clear client state.
    } finally {
      clearSession()
    }
  }, [clearSession])

  useEffect(() => {
    registerUnauthorizedHandler(() => {
      clearSession()
    })

    refresh()
    return () => registerUnauthorizedHandler(null)
  }, [clearSession, refresh])

  const value: AuthContextValue = {
    user,
    isLoading,
    setSession,
    refresh,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider")
  }

  return context
}
